import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import DataTable, {type IDataTableProps} from "./DataTable";
import {PersonalDataDetails} from "./PersonalDataPanel";
import useDialogFocus from "./useDialogFocus";
import {api} from "../api";

interface KycPanelProps extends IDataTableProps {
  canReview: boolean;
  onReview: (record: Record<string, any>, status: "verified" | "rejected", reviewComment: string) => Promise<void>;
}

export default function KycPanel({canReview, onReview, ...tableProps}: KycPanelProps) {
  const {t} = useTranslation();
  const [review, setReview] = useState<{record: Record<string, any>; status: "verified" | "rejected" | "view"} | null>(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const draftKey = useMemo(() => review && review.status !== "view"
    ? `admin:kyc-review:${String(review.record._id || review.record.id || review.record.userId || "unknown")}:${review.record.verificationKind || "identity"}:${review.status}`
    : "", [review]);
  const dirty = Boolean(comment.trim());
  const [enlargedFileId, setEnlargedFileId] = useState<string | null>(null);
  const [imageScale, setImageScale] = useState(1);
  const profileRecord = useMemo(() => {
    const profile = review?.record?.data?.profile || {};
    return {
      userId: review?.record?.userId,
      data: {profile: Object.fromEntries(Object.entries(profile)
        .filter(([key]) => !["accountVerification", "avatar"].includes(key)))},
    };
  }, [review]);

  const isResidence = review?.record?.verificationKind === "residence";
  const verification = isResidence ? review?.record?.data?.residence?.verification : review?.record?.data?.profile?.accountVerification;
  const documents = useMemo(() => [
    verification?.evidenceRef ? {fileId: verification.evidenceRef, fileName: t("kyc.residenceTitle")} : verification?.selfieDocumentPhoto,
    ...(Array.isArray(verification?.supportingDocuments) ? verification.supportingDocuments : []),
  ].filter((document): document is {fileId: string; fileName?: string; mimeType?: string} => Boolean(document?.fileId)), [verification, t]);
  const [documentStates, setDocumentStates] = useState<Record<string, {url?: string; error?: boolean}>>({});

  useEffect(() => {
    setDocumentStates({});
    if (!documents.length) return;
    let cancelled = false;
    for (const document of documents) {
      api.get(`/data/personal_data/verification/documents/${encodeURIComponent(document.fileId)}/download-url`)
        .then(response => {
          if (!cancelled) setDocumentStates(current => ({
            ...current,
            [document.fileId]: response.data?.downloadUrl
              ? {url: response.data.downloadUrl}
              : {error: true},
          }));
        })
        .catch(() => {
          if (!cancelled) setDocumentStates(current => ({...current, [document.fileId]: {error: true}}));
        });
    }
    return () => { cancelled = true; };
  }, [documents]);

  useEffect(() => {
    if (!draftKey) return;
    const saved = localStorage.getItem(draftKey);
    if (saved !== null) setComment(saved);
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey) return;
    if (comment) localStorage.setItem(draftKey, comment);
    else localStorage.removeItem(draftKey);
  }, [comment, draftKey]);

  useEffect(() => {
    if (!review) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  });

  const close = () => {
    if (dirty && !window.confirm(t("forms.discardChanges"))) return;
    setReview(null);
    setEnlargedFileId(null);
    setImageScale(1);
    setComment("");
    setError("");
  };
  const dialogRef = useDialogFocus<HTMLFormElement>(Boolean(review), close);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!review || review.status === "view" || !canReview || review.record.verificationStatus !== "pending" || saving) return;
    if (review.status === "rejected" && !comment.trim()) { setError(t("kyc.commentRequired")); return; }
    setSaving(true); setError("");
    try {
      await onReview(review.record, review.status, comment.trim());
      if (draftKey) localStorage.removeItem(draftKey);
      setReview(null); setComment("");
    }
    catch (requestError: any) { setError(requestError?.response?.data?.message || t("unableLoad")); }
    finally { setSaving(false); }
  };
  return <>
    {tableProps.serverFilter && <label>{t("kyc.verificationKind")}
      <select title={t("kyc.verificationKind")} value={tableProps.serverFilter.field === "verificationKind" ? tableProps.serverFilter.value : "identity"}
        onChange={event => tableProps.serverFilter?.onChange({field: "verificationKind", value: event.target.value})}>
        <option value="identity">{t("kyc.identityTitle")}</option>
        <option value="residence">{t("kyc.residenceTitle")}</option>
      </select>
    </label>}
    <DataTable {...tableProps} includeViewAction={false} defaultColumns={["userId", "verificationStatus", "submittedAt", "reviewedAt"]}
      rowActions={record => [
        {id: "documents", label: t("personalData.userDetails"), icon: "eye"},
      ]}
      onRowAction={(action, record) => {
        if (action !== "documents") return;
        setComment(""); setError(""); setEnlargedFileId(null); setImageScale(1);
        setReview({record, status: "view"});
      }}/>

    {review && <div className="modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}><form ref={dialogRef} tabIndex={-1} className="modal kyc-review" role="dialog" aria-modal="true" onSubmit={submit}>
      <header className="kyc-review-heading">
        <button type="button" className="modal-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={close}>×</button>
        <div><span className="eyebrow">{t("personalData.resources.verification")}</span><h2>{review.status === "view" ? t("personalData.userDetails") : review.status === "verified" ? t("kyc.approve") : t("kyc.reject")}</h2></div>
        {["pending", "verified", "rejected"].includes(review.record.verificationStatus) && <span className={`kyc-status-badge is-${review.record.verificationStatus}`}>{t(`kyc.status.${review.record.verificationStatus}`)}</span>}
      </header>
      {error && <div className="alert" role="alert">{error}</div>}
      <div className="kyc-review-content">
      <section className="kyc-profile-details" aria-label={t("personalData.categories.profile")}>
        <PersonalDataDetails record={profileRecord} showMetadataTags={false} />
        {isResidence && <section><h3>{t("kyc.residenceTitle")}</h3>
          <p>{verification?.verifiedCountry} {verification?.verifiedRegion}</p>
          <p>{t(`kyc.evidenceTypes.${verification?.evidenceType}`)}</p>
          <p>{t("kyc.residenceReviewHint")}</p>
        </section>}
      </section>
      <section className="kyc-evidence" aria-label={t("kyc.viewDocument")}>
      <div className="kyc-evidence-heading"><h3>{t("personalData.categories.documents")}</h3><span>{documents.length}</span></div>
      {enlargedFileId && documentStates[enlargedFileId]?.url && <div className="kyc-image-inspector">
        <div className="kyc-image-controls">
          <button type="button" className="secondary" title={`${t("kyc.zoom")}: −`} aria-label={`${t("kyc.zoom")}: −`} disabled={imageScale <= 0.5} onClick={() => setImageScale(current => Math.max(0.5, current - 0.5))}>−</button>
          <button type="button" className="secondary" title={`${t("kyc.zoom")}: 100%`} onClick={() => setImageScale(1)}>100%</button>
          <button type="button" className="secondary" title={`${t("kyc.zoom")}: +`} aria-label={`${t("kyc.zoom")}: +`} disabled={imageScale >= 4} onClick={() => setImageScale(current => Math.min(4, current + 0.5))}>+</button>
          <output aria-live="polite">{Math.round(imageScale * 100)}%</output>
          <button type="button" className="secondary" title={t("administrators.close")} onClick={() => setEnlargedFileId(null)}>{t("administrators.close")}</button>
        </div>
        <div className="kyc-image-viewport" tabIndex={0} role="region" aria-label={t("kyc.viewDocument")}>
          <img src={documentStates[enlargedFileId].url} alt={documents.find(item => item.fileId === enlargedFileId)?.fileName || t("kyc.selfieDocumentPhoto")} style={{width: `${imageScale * 100}%`}} />
        </div>
      </div>}
      {documents.length > 0 && <div className="kyc-document-list">
        {documents.map((document, index) => {
          const state = documentStates[document.fileId];
          return <div className="kyc-document-preview" key={document.fileId}>
            <strong>{index + 1}. {document.fileName || (index === 0 ? t("kyc.selfieDocumentPhoto") : t("kyc.viewDocument"))}</strong>
            {!state && <span>{t("kyc.documentLoading")}</span>}
            {state?.error && <span className="alert" role="alert">{t("kyc.documentUnavailable")}</span>}
            {state?.url && (
              String(document.mimeType || "").startsWith("image/")
                ? <button type="button" className="kyc-document-enlarge" aria-pressed={enlargedFileId === document.fileId} title={t("kyc.viewDocument")} aria-label={`${t("kyc.viewDocument")}: ${document.fileName || index + 1}`} onClick={() => {setEnlargedFileId(document.fileId); setImageScale(1);}}>
                    <img src={state.url} alt={document.fileName || t("kyc.selfieDocumentPhoto")} className="kyc-document-preview__image"/>
                  </button>
                : <a href={state.url} target="_blank" rel="noreferrer">{t("kyc.viewDocument")}</a>
            )}
          </div>;
        })}
      </div>}
      </section></div>
      {review.status === "view" && canReview && review.record.verificationStatus === "pending" && <div className="kyc-review-decisions">
        <button type="button" className="primary" title={t("kyc.approve")} onClick={() => setReview({...review, status: "verified"})}>{t("kyc.approve")}</button>
        <button type="button" className="danger-button" title={t("kyc.reject")} onClick={() => setReview({...review, status: "rejected"})}>{t("kyc.reject")}</button>
      </div>}
      {review.status !== "view" && <label>{t("kyc.reviewComment")}<textarea value={comment} onChange={event => setComment(event.target.value)} required={review.status === "rejected"}/></label>}
      <div className="modal-actions"><button type="button" className="secondary" onClick={close}>{t(review.status === "view" ? "administrators.close" : "forms.cancel")}</button>{review.status !== "view" && <button type="submit" className={review.status === "rejected" ? "danger-button" : "primary"} disabled={saving || (review.status === "rejected" && !comment.trim())}>{t("forms.confirm")}</button>}</div>
    </form></div>}
  </>;
}
